// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import Axios from 'axios';
import { injectable } from 'inversify';

import { Customer, CustomerDTO, CustomerUser, CustomerUserDTO } from '@shared/models/customers';
import { ListRequestParams } from '@shared/types/services';
import BaseService from '@core/services/base';
import { CUSTOMER_FIELDS } from '@shared/constants/customer';
import { CUSTOMER_USER_FIELDS } from '@shared/constants/customer';
import { ActivityFilter, REQUEST_PARAMS } from '@shared/constants/services';

@injectable()
export default class CustomersService extends BaseService<CustomerDTO, Customer> {
  static diToken = Symbol('customers-service');

  constructor() {
    super({ domainArea: 'customers', Model: Customer });
  }

  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        customers(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { field: ${sorting?.field}, type: ${sorting?.type} },
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
          ${REQUEST_PARAMS.filtering.fields.activityFilter}: ${filtering?.activityFilter ?? ActivityFilter.all},
          ${REQUEST_PARAMS.filtering.fields.isBlocked}: ${filtering?.isBlocked ?? null}
        ) {
          items {
            ${CUSTOMER_FIELDS.name},
            ${CUSTOMER_FIELDS.code},
            ${CUSTOMER_FIELDS.ordersAmount},
            ${CUSTOMER_FIELDS.jobRequestsAmount},
            ${CUSTOMER_FIELDS.assetsAmount},
            ${CUSTOMER_FIELDS.customerUsersAmount},
            ${CUSTOMER_FIELDS.id},
            ${CUSTOMER_FIELDS.isBlocked},
            ${CUSTOMER_FIELDS.isActive}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  getUsersListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      query CustomerUsersQuery($customerId: Int!) {
        users(
          customerId: $customerId, 
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { field: ${sorting?.field}, type: ${sorting?.type} },
          ${REQUEST_PARAMS.filtering.fields.activityFilter}: ${filtering?.activityFilter ?? ActivityFilter.all}
        ) {
          items {
            ${CUSTOMER_USER_FIELDS.fullName},
            ${CUSTOMER_USER_FIELDS.email},
            ${CUSTOMER_USER_FIELDS.title},
            ${CUSTOMER_USER_FIELDS.profilePhoto},
            ${CUSTOMER_USER_FIELDS.id},
            ${CUSTOMER_USER_FIELDS.lastActivityDate},
            ${CUSTOMER_USER_FIELDS.profileStatus},
            ${CUSTOMER_USER_FIELDS.isBlocked},
            ${CUSTOMER_FIELDS.isActive}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  async getUsersList(customerId: CustomerDTO['id'], queries: ListRequestParams) {
    const url = this.getCustomerUserURL(customerId);

    const { data: { users: { items, pageInfo } } } = await Axios.get(
      url,
      { params: { query: this.getUsersListQuery(queries) } }
    );

    return {
      pageInfo,
      items: items.map((dto: CustomerUserDTO) => new CustomerUser(dto))
    };
  }

  async getUser(customerId: CustomerDTO['id'], userId: CustomerUserDTO['id']): Promise<CustomerUser> {
    const url = this.getCustomerUserURL(customerId, userId);
    const { data } = await Axios.get(url);

    return new CustomerUser(data);
  }

  async createUser(customerId: CustomerDTO['id'], data: CustomerUserDTO): Promise<void> {
    const url = this.getCustomerUserURL(customerId);

    return Axios.post(url, data);
  }

  editUser(customerId: CustomerDTO['id'], userId: CustomerUserDTO['id'], data: CustomerUserDTO): Promise<void> {
    const url = this.getCustomerUserURL(customerId, userId);

    return Axios.put(url, data);
  }

  deleteUser(customerId: CustomerDTO['id'], userId: CustomerUserDTO['id']): Promise<void> {
    const url = this.getCustomerUserURL(customerId, userId);

    return Axios.delete(url);
  }

  getCustomerUserURL = (customerId: CustomerDTO['id'], userId?: CustomerUserDTO['id']) => {
    return `${this.urlPrefix}/${customerId}/users/${userId || ''}`;
  }

  resendInvitation(userId: CustomerUserDTO['id']) {
    const url = `users/${userId}/invitation/resend`;

    return Axios.post(url);
  }
}
